/**
 * Reports vetting service — analyst-portal owned.
 *
 * Calls the analyst-api proxy that forwards to func-axn-operations-api. The
 * write/list/detail logic for reports lives in ops-api; analysts vet what the
 * writing agent produces (review checklist + custom content + approve/reject)
 * via this service.
 */

import { apiFetch, apiPost, apiDelete, buildQueryString, type ApiResponse } from './api';
import type {
  ReportSummary,
  ReportDetail,
  ReportFilters,
  UpdateReportRequest,
  ReviewReportRequest,
  ApproveReportRequest,
  RejectReportRequest,
  RegenerateReportRequest,
  ReportStatus,
  ReportType,
} from '../data/types';

// ============================================================================
// Response wrappers
// ============================================================================

interface PaginatedReportsResponse {
  reports: ReportSummary[];
  pagination: { total: number; limit: number; offset: number };
}

// ============================================================================
// Queue + detail
// ============================================================================

export async function getReports(
  filters?: ReportFilters,
): Promise<ApiResponse<PaginatedReportsResponse>> {
  const query = buildQueryString(filters || {});
  return apiFetch<PaginatedReportsResponse>(`/reports${query}`);
}

export async function getReport(reportId: string): Promise<ApiResponse<ReportDetail>> {
  return apiFetch<ReportDetail>(`/reports/${reportId}`);
}

export async function updateReport(
  reportId: string,
  data: UpdateReportRequest,
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  return apiFetch<{ success: boolean; message: string }>(`/reports/${reportId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteReport(
  reportId: string,
  options: { force?: boolean } = {},
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  const qs = options.force ? '?force=true' : '';
  return apiDelete<{ success: boolean; message: string }>(`/reports/${reportId}${qs}`);
}

export interface ExportPdfResult {
  pdfUrl: string;
  blobPath: string;
  sizeBytes: number;
  reportId: string;
  durationMs: number;
  pageCount: number;
  contentHash: string;
  templateId: string;
  templateVersion: number;
}

/**
 * Render the report to PDF via func-axn-report-pdf (headless Chromium against
 * the /reports/{id}/print route). Slow call — cold Chromium can take ~30s.
 */
export async function exportReportPdf(reportId: string): Promise<ApiResponse<ExportPdfResult>> {
  return apiPost<ExportPdfResult>(`/reports/${reportId}/pdf`, {});
}

export async function getReportPdf(reportId: string): Promise<ApiResponse<{ pdfUrl: string }>> {
  return apiFetch<{ pdfUrl: string }>(`/reports/${reportId}/pdf`);
}

/**
 * Pre-warm the PDF renderer (Flex Consumption scales to zero; a cold first
 * render can exceed the SWA's 45s edge timeout). Fire-and-forget on report
 * open so the Export click lands on a warm instance.
 */
export async function warmupPdfRenderer(): Promise<void> {
  await apiFetch<{ status?: string }>('/reports/pdf-warmup').catch(() => undefined);
}

// ============================================================================
// Lifecycle actions
// ============================================================================

export async function reviewReport(
  reportId: string,
  data: ReviewReportRequest,
): Promise<
  ApiResponse<{ success: boolean; message: string; allItemsChecked: boolean }>
> {
  return apiPost(`/reports/${reportId}/review`, data);
}

export async function approveReport(
  reportId: string,
  data?: ApproveReportRequest,
): Promise<
  ApiResponse<{
    success: boolean;
    message: string;
    approvedBy: string;
    approvedAt: string;
    sentAt: string;
  }>
> {
  // Approve publishes in the same step — the report is live on the customer
  // portal when this succeeds.
  return apiPost(`/reports/${reportId}/approve`, data || {});
}

export async function rejectReport(
  reportId: string,
  data: RejectReportRequest,
): Promise<
  ApiResponse<{
    success: boolean;
    message: string;
    rejectedBy: string;
    rejectedAt: string;
    reason: string;
  }>
> {
  return apiPost(`/reports/${reportId}/reject`, data);
}

export async function regenerateReport(
  reportId: string,
  data?: RegenerateReportRequest,
): Promise<ApiResponse<{ success: boolean; message: string; reportId: string }>> {
  return apiPost(`/reports/${reportId}/regenerate`, data || {});
}

/** Publishes a report approved before approve-publishes landed. */
export async function publishReport(
  reportId: string,
): Promise<ApiResponse<{ success: boolean; message: string; sentAt: string }>> {
  return apiPost(`/reports/${reportId}/send`, {});
}

export interface AskSectionResult {
  answer: string;
  runId: string;
  tokenCount: number | null;
}

/**
 * Ask the writing agent a free-form question about a specific section.
 * Returns plain-text answer (no markdown rendering, just paragraphs).
 */
export async function askReportSection(
  reportId: string,
  sectionKey: string,
  question: string,
): Promise<ApiResponse<AskSectionResult>> {
  return apiPost<AskSectionResult>(
    `/reports/${reportId}/sections/${encodeURIComponent(sectionKey)}/ask`,
    { question },
  );
}

// ============================================================================
// Display helpers
// ============================================================================

export function getStatusLabel(status: ReportStatus): string {
  const labels: Record<ReportStatus, string> = {
    generating: 'Generating',
    draft: 'Draft',
    under_review: 'Under Review',
    approved: 'Approved',
    rejected: 'Rejected',
    sent: 'Published',
  };
  return labels[status] ?? status;
}

// Maps to one of swa-axn-analyst's BadgeVariant values.
export function getStatusColor(
  status: ReportStatus,
): 'blue' | 'default' | 'yellow' | 'green' | 'red' {
  const colors: Record<ReportStatus, 'blue' | 'default' | 'yellow' | 'green' | 'red'> = {
    generating: 'blue',
    draft: 'default',
    under_review: 'yellow',
    approved: 'green',
    rejected: 'red',
    sent: 'green',
  };
  return colors[status] ?? 'default';
}

export function getReportTypeLabel(type: ReportType): string {
  const labels: Record<ReportType, string> = {
    monthly: 'Monthly Report',
    weekly: 'Weekly Summary',
    ad_hoc: 'Ad-hoc',
    executive: 'Executive Summary',
    compliance: 'Compliance Report',
  };
  return labels[type] ?? type;
}
